import { useState, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { printHtmlInIframe } from '~/services/printer/printerUtils';
import htmlToPrintService, {
  PRINT_TYPES,
  MOCK_NAMES,
} from '../services/htmlToPrintService';

export const useHtmlToPrint = () => {
  const [selectedPrintType, setSelectedPrintType] = useState('');
  const [itemId, setItemId] = useState('');
  const [selectedMockName, setSelectedMockName] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [error, setError] = useState(null);

  const iframeRef = useRef(null);

  const resetForm = useCallback(() => {
    setSelectedPrintType('');
    setItemId('');
    setSelectedMockName('');
    setHtmlContent('');
    setError(null);
  }, []);

  const clearHtmlContent = useCallback(() => {
    setHtmlContent('');
    setError(null);
  }, []);

  const fetchHtmlContent = useCallback(async () => {
    const hasRegularForm = selectedPrintType && itemId;
    const hasMockForm = selectedMockName;
    if (!hasRegularForm && !hasMockForm) {
      setError('Preencha um tipo de impressão + ID ou selecione um mock');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      htmlToPrintService.validatePrintRequest({
        print_type: selectedPrintType,
        id: itemId,
        mock_name: selectedMockName,
      });
      const htmlResponse = await htmlToPrintService.fetchPrintableHtml({
        print_type: selectedPrintType,
        id: itemId,
        mock_name: selectedMockName,
      });
      setHtmlContent(htmlResponse);
      toast.success('HTML carregado com sucesso!');
    } catch (err) {
      const errorMessage = err.message || 'Erro ao buscar conteúdo HTML';
      setError(errorMessage);
      toast.error(errorMessage);
      setHtmlContent('');
    } finally {
      setIsLoading(false);
    }
  }, [selectedPrintType, itemId, selectedMockName]);

  const printHtml = useCallback(async () => {
    if (!htmlContent) {
      toast.error('Nenhum conteúdo HTML para imprimir');
      return;
    }
    setIsPrinting(true);
    try {
      printHtmlInIframe(iframeRef, htmlContent);
      toast.success('Impressão enviada!');
    } catch (err) {
      const errorMessage = err.message || 'Erro ao imprimir';
      toast.error(errorMessage);
    } finally {
      setTimeout(() => {
        setIsPrinting(false);
      }, 1000);
    }
  }, [htmlContent]);

  const handlePrintTypeChange = useCallback(
    (value) => {
      setSelectedPrintType(value);
      clearHtmlContent();
    },
    [clearHtmlContent]
  );

  const handleIdChange = useCallback(
    (value) => {
      setItemId(value);
      clearHtmlContent();
    },
    [clearHtmlContent]
  );

  const handleMockNameChange = useCallback(
    (value) => {
      setSelectedMockName(value);
      clearHtmlContent();
    },
    [clearHtmlContent]
  );

  const printTypeOptions = htmlToPrintService.getPrintTypeOptions();
  const mockNameOptions = htmlToPrintService.getMockNameOptions();
  const currentPrintTypeLabel = selectedPrintType
    ? htmlToPrintService.getPrintTypeLabel(selectedPrintType)
    : '';
  const currentMockLabel = selectedMockName
    ? htmlToPrintService.getMockNameLabel(selectedMockName)
    : '';
  const isFormValid = (selectedPrintType && itemId) || selectedMockName;
  const canFetchHtml = isFormValid && !isLoading;
  const canPrint = htmlContent && !isPrinting;

  return {
    // State
    selectedPrintType,
    itemId,
    selectedMockName,
    htmlContent,
    isLoading,
    isPrinting,
    error,
    // Computed values
    printTypeOptions,
    mockNameOptions,
    currentPrintTypeLabel,
    currentMockLabel,
    isFormValid,
    canFetchHtml,
    canPrint,
    // Actions
    handlePrintTypeChange,
    handleIdChange,
    handleMockNameChange,
    fetchHtmlContent,
    printHtml,
    resetForm,
    clearHtmlContent,
    // Refs
    iframeRef,
    // Constants for external use
    PRINT_TYPES,
    MOCK_NAMES,
  };
};
